import React from "react";
import { useSelector } from "react-redux";
import QRCode from "react-qr-code";
import { formatMoney } from "./formatMoney";
import { useImage } from "../contexts/ImageContext";
import "../styles/receipt.css";

const Receipt = ({ order }) => {
  const { settings } = useSelector((state) => state.site);
  const { getImageUrl } = useImage();

  // Early return if no order
  if (!order) {
    return <div className="receipt-container-new">No order data available</div>;
  }
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const subtotal = order.total_price || 0;
  const shippingFee = order.shipping_fees ? parseFloat(order.shipping_fees) : 0;
  const deliveryFee = 0; // Delivery fee is typically 0
  const couponDiscount = order.coupon_discount_amount
    ? parseFloat(order.coupon_discount_amount)
    : 0;
  const finalTotal = order.final_total
    ? parseFloat(order.final_total)
    : subtotal + shippingFee - couponDiscount;

  // Generate KOD ID from order date and time
  const orderDate = new Date(order.created_at);
  const year = orderDate.getFullYear().toString().substring(2);
  const month = String(orderDate.getMonth() + 1).padStart(2, "0");
  const day = String(orderDate.getDate()).padStart(2, "0");
  const hour = String(orderDate.getHours()).padStart(2, "0");
  const minute = String(orderDate.getMinutes()).padStart(2, "0");
  const kodId = `KOD${year}${month}${day}${hour}${minute}`;

  // Generate receipt number
  const receiptNumber = `REF${orderDate.getFullYear()}${month}/KOD${String(
    order.order_id.substring(order.order_id.length - 3)
  ).padStart(3, "0")}`;

  // Transaction ID from payment or use order ID
  const transactionId =
    order.payment?.transaction_id ||
    order.order_id.substring(0, 4) +
      "-" +
      order.order_id.substring(4, 8) +
      "-" +
      order.order_id.substring(8, 12);

  // Payment method
  const paymentMethod =
    order.payment?.payment_method || order.payment_status === "Paid"
      ? "Mobile Money"
      : "Pending";

  // QR Code data - can include order ID and other info
  const qrCodeData = JSON.stringify({
    order_id: order.order_id,
    date: formatDate(order.created_at),
    total: finalTotal,
  });

  return (
    <div className="receipt-container-new">
      {/* Header with Logo and Company Info */}
      <div className="receipt-header-new">
        <div className="receipt-logo-section">
          {settings?.light_logo ? (
            <img
              src={getImageUrl(settings.light_logo)}
              alt="K-Shop"
              className="receipt-logo-image"
            />
          ) : (
            <div className="receipt-logo-circle">
              <span className="receipt-logo-text">K-Shop</span>
            </div>
          )}
        </div>
        <div className="receipt-company-info">
          <h1 className="receipt-company-name">
            {settings?.name || "K-Shop"}
          </h1>
          <p className="receipt-company-address">
            {settings?.address || "KG 11 Ave, KISIMENT, KIGALI"}
          </p>
          <p className="receipt-company-contact">
            {settings?.phone || "+250 793 761 875"}
          </p>
          <p className="receipt-company-website">
            {typeof window !== "undefined" ? window.location.host : "k-shop.com"}
          </p>
          <p className="receipt-company-tin">TIN: 121143548</p>
        </div>
      </div>

      <div className="receipt-dashed-line"></div>

      {/* Purchase Receipt Title */}
      <h2 className="receipt-purchase-title">Purchase Receipt</h2>

      <div className="receipt-dashed-line"></div>

      {/* Client Information */}
      <div className="receipt-client-section">
        <p className="receipt-client-name">
          <strong>Client Name:</strong> {order.user?.name || "N/A"}
        </p>
        <p className="receipt-client-email">
          <strong>E-mail:</strong> {order.user?.email || "N/A"}
        </p>
        <p className="receipt-client-phone">
          <strong>Phone:</strong> {order.user?.phone || "N/A"}
        </p>
      </div>

      <div className="receipt-dashed-line"></div>

      {/* Order List */}
      <div className="receipt-order-list-section">
        <h3 className="receipt-order-list-title">Order list:</h3>
        {order.order_items && order.order_items.length > 0 ? (
          order.order_items.map((item, index) => {
            const itemTotal = item.quantity * item.price_at_purchase;
            return (
              <div key={item.order_item_id} className="receipt-order-item">
                <p className="receipt-item-number">{index + 1}.</p>
                <div className="receipt-item-details">
                  <p className="receipt-item-name-new">
                    {item.product?.name || "Product"}
                    {item.option && `, ${item.option.name}`}
                    {item.size && `, Size: ${item.size.name}`}
                    {item.selected_color && `, Color: ${item.selected_color}`}
                  </p>
                  <div className="receipt-item-pricing">
                    <span className="receipt-item-unit-price">
                      {formatMoney(item.price_at_purchase)}
                    </span>
                    <span className="receipt-item-quantity-new">
                      x {item.quantity} x
                    </span>
                    <span className="receipt-item-total-new">
                      {formatMoney(itemTotal)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p>No items found</p>
        )}
      </div>

      <div className="receipt-dashed-line"></div>

      {/* Summary Section */}
      <div className="receipt-summary-section-new">
        <div className="receipt-summary-row-new">
          <span>Total:</span>
          <span>{formatMoney(subtotal)}</span>
        </div>
        <div className="receipt-summary-row-new">
          <span>Shipping fee:</span>
          <span>{formatMoney(shippingFee)}</span>
        </div>
        <div className="receipt-summary-row-new">
          <span>Delivery fee:</span>
          <span>{formatMoney(deliveryFee)}</span>
        </div>
        <div className="receipt-dashed-line"></div>
        <div className="receipt-summary-row-new receipt-grand-total">
          <span>Grand TOTAL:</span>
          <span>{formatMoney(finalTotal)}</span>
        </div>
        <div className="receipt-dashed-line"></div>
        <div className="receipt-summary-row-new">
          <span>Payment MODE:</span>
          <span>{paymentMethod}</span>
        </div>
        <div className="receipt-summary-row-new">
          <span>Items number:</span>
          <span>{order.total_products || order.order_items?.length || 0}</span>
        </div>
      </div>

      <div className="receipt-dashed-line"></div>

      {/* KOD Information */}
      <div className="receipt-kod-section">
        <h3 className="receipt-kod-title">KOD Information</h3>
        <div className="receipt-kod-details">
          <div className="receipt-kod-row">
            <span>Date:</span>
            <div className="receipt-kod-date-time">
              <span>{formatDate(order.created_at)}</span>
              <span className="receipt-kod-time">
                {formatTime(order.created_at)}
              </span>
            </div>
          </div>
          <div className="receipt-kod-row">
            <span>KOD ID:</span>
            <span>{kodId}</span>
          </div>
          <div className="receipt-kod-row">
            <span>Transaction ID:</span>
            <span>{transactionId}</span>
          </div>
          <div className="receipt-kod-row">
            <span>Receipt numberID:</span>
            <span>{receiptNumber}</span>
          </div>
        </div>
      </div>

      {/* QR Code */}
      <div className="receipt-footer-section">
        <div className="receipt-qr-section">
          <QRCode
            value={qrCodeData}
            size={120}
            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            viewBox={`0 0 120 120`}
          />
        </div>
      </div>

      <div className="receipt-dashed-line"></div>

      {/* Thank You Message */}
      <div className="receipt-thank-you">
        <p>Thank you for shopping with Us!</p>
      </div>
    </div>
  );
};

export default Receipt;
